/* eslint-disable prettier/prettier */
import { Approver } from '@/app/models/approver';
import { Cell } from '@/app/models/cell';
import { Cluster } from '@/app/models/cluster';
import { PaginationResponse } from '@/app/models/pagination';
import { PersonResponse } from '@/app/models/person';
import { ResultCenter } from '@/app/models/resultCenter';
import {
  useAddApproverByClusterAndCellMutation,
  useAddApproverByClusterMutation,
  useAddApproverByProjectMutation,
  useDeleteApproverMutation,
  useDeleteByClusterAndCellMutation,
  useDeleteByClusterMutation,
  useSearchApproversQuery,
} from '@/app/services/approvers';
import { useSearchCellQuery } from '@/app/services/cell';
import { useSearchClusterQuery } from '@/app/services/clusterApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import { useSearchResultCenterQuery } from '@/app/services/resultCenterApi';
import { useSystemSearchQuery } from '@/app/services/systemsApi';
import ApproversTable from '@/components/approvers/ApproversTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import PageTitle from '@/components/common/pageTitle';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function Approvers() {
  const { t } = useTranslation('approvers');
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteBulkDialog, setDeleteBulkDialog] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState<Approver | null>(null);

  const [bringAll, setBringAll] = useState(false);

  // search for filtering by system
  const [filterSystem, setFilterSystem] = useState<number | null>(-1);
  const { data: systems } = useSystemSearchQuery({
    bringAll: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  // search for filtering by person
  const [person, setperson] = useState<PersonResponse | null>(null);
  const [personSearch, setpersonSearch] = useState('');
  const { data: persons, isFetching: isLoadingCollaborator } = useSearchPersonQuery(
    {
      searchTerm: personSearch,
      page: 0,
      size: 10,
      sortKey: '',
    },
    {
      skip: personSearch.length < 1,
    },
  );

  // search for filtering by result center
  const [resultCenter, setResultCenter] = useState<ResultCenter | null>(null);
  const [resultCenterSearch, setResultCenterSearch] = useState('');
  const { data: resultCenters, isFetching: isLoadingResultCenter } = useSearchResultCenterQuery(
    {
      bringClose: bringAll,
      searchTerm: resultCenterSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
    },
    {
      skip: resultCenterSearch.length < 1,
    },
  );

  // search for filtering by cluster
  const [cluster, setCluster] = useState<Cluster | null>(null);
  const [clusterSearch, setClusterSearch] = useState('');
  const { data: clusters, isFetching: isLoadingCluster } = useSearchClusterQuery(
    {
      searchTerm: clusterSearch,
      bringAll,
      page: 0,
      size: 10,
      sortKey: '',
    },
    {
      skip: clusterSearch.length < 1,
    },
  );

  // search for filtering by cell
  const [cell, setCell] = useState<Cell | null>(null);
  const [cellSearch, setCellSearch] = useState('');
  const { data: cells, isFetching: isLoadingCell } = useSearchCellQuery(
    {
      searchTerm: cellSearch,
      bringAll,
      page: 0,
      size: 10,
      sortKey: '',
    },
    {
      skip: cellSearch.length < 1,
    },
  );

  const { data: approvers } = useSearchApproversQuery(
    {
      systemId: filterSystem || -1,
      personId: person?.id || 0,
      projectId: resultCenter?.id,
      clusterCode: cluster?.code,
      cellCode: cell?.code,
      page: 0,
      size: 10,
      sortKey: 'projectName',
    },
    { skip: (filterSystem == null || filterSystem < 0) && !resultCenter && !cluster && !cell && !person?.id },
  );

  const getAddLabel = () => {
    if (resultCenter) {
      return t('add.by.result.center');
    }
    if (cell) {
      return t('add.by.cell');
    }
    if (cluster) {
      return t('add.by.cluster');
    }
    return t('add.permission');
  };

  const getRemoveLabel = () => {
    if (cell) {
      return t('remove.by.cell');
    }
    if (cluster) {
      return t('remove.by.cluster');
    }
    return t('remove.in.bulk');
  };

  const [addApproverByProject] = useAddApproverByProjectMutation();
  const [addApproverByCluster] = useAddApproverByClusterMutation();
  const [addApproversByClusterAndCell] = useAddApproverByClusterAndCellMutation();

  const handleSubmit = async () => {
    if (!filterSystem || !person) {
      return;
    }
    if (resultCenter) {
      // add the permission by resultCenter
      await toast.promise(
        addApproverByProject({
          systemId: filterSystem,
          personId: person.id,
          projectId: resultCenter.id,
        }).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('permission.added') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
    } else if (cell && cluster) {
      // add the permission by cluster and cell
      await toast.promise(
        addApproversByClusterAndCell({
          systemId: filterSystem,
          personId: person.id,
          clusterCode: cluster.code,
          cellCode: cell.code,
        }).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('permission.added') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
    } else if (cluster) {
      // add the permission only by cluster
      await toast.promise(
        addApproverByCluster({
          systemId: filterSystem,
          personId: person.id,
          clusterCode: cluster.code,
        }).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('permission.added') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
    }
  };

  const [deleteByCluster] = useDeleteByClusterMutation();
  const [deleteByClusterAndCell] = useDeleteByClusterAndCellMutation();
  const handleRemoveInBulk = async () => {
    if (!filterSystem || !person) {
      return;
    }
    if (!cell && !cluster) {
      return;
    }
    if (cell && cluster) {
      // remove by cell and cluster
      await toast.promise(
        deleteByClusterAndCell({
          systemId: filterSystem,
          personId: person.id,
          clusterCode: cluster?.code,
          cellCode: cell?.code,
        }).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('permission.removed') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
    } else if (cluster) {
      // remove by cluster
      await toast.promise(
        deleteByCluster({
          systemId: filterSystem,
          personId: person.id,
          clusterCode: cluster?.code,
        }).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('permission.removed') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
    }
  };

  const [deleteApprover] = useDeleteApproverMutation();

  const handleDelete = async () => {
    if (!selectedApprover || !selectedApprover?.id) {
      return;
    }
    await toast.promise(
      deleteApprover(selectedApprover?.id).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('permission.deleted') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setSelectedApprover(null);
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginRight: '15px',
      }}
    >
      <PageTitle
        title={t('approvers')}
        titleLabel={`( ${t('by.product')} )` || ''}
        subtitle={t('approvers.subtitle') || ''}
      />

      <Paper elevation={2} sx={{ p: 3, my: 2 }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexBasis: '20%' }}>
              <Typography variant="fieldHeader">{t('system')}</Typography>
              <FormControl>
                <Select
                  value={filterSystem}
                  onChange={(e) => {
                    if (e.target.value) {
                      setFilterSystem(e.target.value as number);
                    }
                  }}
                  sx={{
                    height: 32,
                    fontSize: 14,
                    paddingTop: 0,
                  }}
                >
                  <MenuItem value={-1} sx={{ fontSize: 13 }}>
                    {t('choose.a.system')}
                  </MenuItem>
                  {systems?.content.map((system) => (
                    <MenuItem key={system.id} value={system.id} sx={{ fontSize: 13 }}>
                      {system.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexBasis: '40%' }}>
              <Typography variant="fieldHeader">{t('collaborator')}</Typography>
              <GenericAutoComplete
                value={person}
                onChange={(value) => {
                  setperson(value);
                }}
                disabled={!!filterSystem && filterSystem < 0}
                equalityTest={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => (option ? `${option?.badge ?? ''} - ${option.name ?? ''}` : '')}
                onSearch={(value) => {
                  setpersonSearch(value);
                }}
                options={personSearch !== '' && persons?.content ? persons.content : []}
                placeholder={t('type.to.search.collaborator') as string}
                loading={isLoadingCollaborator}
              />
            </Box>
            <FormControlLabel
              sx={{ ml: 'auto', alignSelf: 'flex-end', display: 'flex' }}
              control={
                <Checkbox
                  color="secondary"
                  checked={bringAll}
                  onChange={() => {
                    setBringAll(!bringAll);
                  }}
                />
              }
              label={
                <Typography variant="body1" fontWeight="bold">
                  {t('include.closed.projects.clusters.and.cells')}
                </Typography>
              }
            />
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexBasis: '40%' }}>
              <Typography variant="fieldHeader">{t('result.center')}</Typography>
              <GenericAutoComplete
                value={resultCenter}
                onChange={(value) => {
                  setResultCenter(value);
                }}
                disabled={!!cluster || (!!filterSystem && filterSystem < 0)}
                equalityTest={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) =>
                  option
                    ? `${option?.code ?? ''} - ${option.name ?? ''} ${option?.status === 'CLOSED' ? ' (Fechado)' : ''}`
                    : ''
                }
                onSearch={(value) => {
                  setResultCenterSearch(value);
                }}
                options={resultCenterSearch !== '' && resultCenters?.content ? resultCenters.content : []}
                placeholder={t('type.to.search.result.center') as string}
                loading={isLoadingResultCenter}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexBasis: '30%' }}>
              <Typography variant="fieldHeader">{t('cluster')}</Typography>
              <GenericAutoComplete
                value={cluster}
                onChange={(value) => {
                  setCluster(value);
                }}
                disabled={!!resultCenter || (!!filterSystem && filterSystem < 0)}
                equalityTest={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) =>
                  option
                    ? `${option?.code ?? ''} - ${option.name ?? ''} ${option?.active === false ? ' (Fechado)' : ''}`
                    : ''
                }
                onSearch={(value) => {
                  setClusterSearch(value);
                }}
                options={clusterSearch !== '' && clusters?.content ? clusters.content : []}
                placeholder={t('type.to.search.cluster') as string}
                loading={isLoadingCluster}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexBasis: '30%' }}>
              <Typography variant="fieldHeader">{t('cell')}</Typography>
              <GenericAutoComplete
                value={cell}
                onChange={(value) => {
                  setCell(value);
                }}
                disabled={!!resultCenter || !cluster}
                equalityTest={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) =>
                  option
                    ? `${option?.code ?? ''} - ${option.name ?? ''} ${option?.active === false ? ' (Fechado)' : ''}`
                    : ''
                }
                onSearch={(value) => {
                  setCellSearch(value);
                }}
                options={cellSearch !== '' && cells?.content ? cells.content : []}
                placeholder={t('type.to.search.cell') as string}
                loading={isLoadingCell}
              />
            </Box>
          </Box>
          <Box sx={{ ml: 'auto', alignSelf: 'flex-end', display: 'flex' }}>
            <Button
              variant="primary"
              disabled={(!cell && !cluster) || !person || !filterSystem}
              onClick={() => {
                setDeleteBulkDialog(true);
              }}
            >
              {getRemoveLabel()}
            </Button>
            <Button
              variant="primary"
              disabled={(!resultCenter && !cell && !cluster) || !person || !filterSystem}
              sx={{ ml: 2 }}
              onClick={() => {
                handleSubmit();
              }}
            >
              {getAddLabel()}
            </Button>
          </Box>
        </Box>
      </Paper>

      <ApproversTable
        onDelete={() => setDeleteDialog(true)}
        setSelectedApprover={setSelectedApprover}
        data={approvers as PaginationResponse<Approver>}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
      />
      <CancelOrConfirmDialog
        open={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
        }}
        onConfirm={() => {
          handleDelete();
          setDeleteDialog(false);
        }}
        title={t('confirm.delete') || ''}
        contentText={
          t('confirm.delete.subtitle', {
            collaborator: selectedApprover?.person?.name,
            system: selectedApprover?.system.name,
            resultCenter: `${selectedApprover?.project.code} - ${selectedApprover?.project.name}`,
          }) || ''
        }
        confirmText={t('remove') || ''}
        cancelText={t('common:cancel') || ''}
      />

      <CancelOrConfirmDialog
        open={deleteBulkDialog}
        onClose={() => {
          setDeleteBulkDialog(false);
        }}
        onConfirm={() => {
          handleRemoveInBulk();
          setDeleteBulkDialog(false);
        }}
        title={t('confirm.delete') || ''}
        contentText={
          cell
            ? (t('confirm.delete.bulk.cell.subtitle', {
                collaborator: person?.name,
                system: systems?.content?.[filterSystem || 0]?.name,
                cell: cell.name,
                interpolation: { escapeValue: false },
              }) as string)
            : (t('confirm.delete.bulk.cluster.subtitle', {
                collaborator: person?.name,
                system: systems?.content?.[filterSystem || 0]?.name,
                cluster: cluster?.name,
                interpolation: { escapeValue: false },
              }) as string)
        }
        confirmText={t('remove') || ''}
        cancelText={t('common:cancel') || ''}
      />
    </div>
  );
}

export default Approvers;
